import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isObject } from 'lodash';
import { SlideGroupContext } from './SlideGroupContext';
import { Direction, TRANSITION_DURATION, queue, delay, hideAppLayer, getNextSlide } from './SlideNavigation';
import { EntryPoint, SlideType } from '../../models';
import { ABUtils, getHashId, pushBrowserHistory, resetHash } from '../../utils';

const defaultMapByProduct = (isHotel) => {
    return isHotel ?
        new Map([[SlideType.ARRIVAL, {}], [SlideType.DATE]]) :
        new Map([[SlideType.DEPARTURE, {}], [SlideType.ARRIVAL, {}], [SlideType.DATE]]);
};

const createDefaultSlideGroup = (isHotel) => {
    return {
        slides: defaultMapByProduct(isHotel),
        history: [],
        active: null,
        scrollY: 0,
    };
};

export const SlideGroupContextProvider = ({ children }) => {
    const [startedWithEmptyFields, setStartedWithEmptyFields] = useState(false);
    const isHotel = useSelector((state) => state.activeProduct && state.activeProduct.activeProduct === EntryPoint.HOTELS);

    const slideGroup = useMemo(() => createDefaultSlideGroup(isHotel), [isHotel]);

    useEffect(() => {
        if (Object.values(SlideType).includes(getHashId())) {
            resetHash();
        }
        window.addEventListener('hashchange', onHashChange);

        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    const onHashChange = () => {
        const hashId = getHashId();
        if (!slideGroup.active) {
            if (!slideGroup.slides.get(hashId)) {
                hideAppLayer();
            }
            return;
        }
        const nextSlide = getNextSlide(slideGroup.slides, slideGroup.active);
        if (nextSlide && nextSlide.id === hashId && !slideGroup.history.includes(nextSlide.id)) {
            return queue(next());
        }
        if (slideGroup.history.length > 0 && slideGroup.active !== hashId) {
            return queue(prev());
        }
        if (slideGroup.history.length === 0 && !!hashId) {
            resetHash();
        }
    };

    const getCurrent = () => slideGroup;

    const register = (slide) => slideGroup.slides.set(slide.id, slide);

    const open = (id) => {
        const slide = slideGroup.slides.get(id);
        slide.setDirection(Direction.VERTICAL);
        slide.setActive(true);

        slideGroup.active = id;
        slideGroup.history.push(id);
        pushBrowserHistory(id);
        slideGroup.scrollY = window.scrollY;

        const { isReactGeolocationByIp } = ABUtils.getABs();
        const slidesEmpty = [...slideGroup.slides.values()].filter((s) => isObject(s) && s.isEmpty).map((s) => s.id);
        let startedEmpty = slidesEmpty.includes(SlideType.ARRIVAL) && slidesEmpty.includes(SlideType.DATE);
        if (!isReactGeolocationByIp && !isHotel) {
            startedEmpty = startedEmpty && slidesEmpty.includes(SlideType.DEPARTURE);
        }
        setStartedWithEmptyFields(startedEmpty);
    };

    const clickOpen = (id) => queue(open(id));

    const close = () => {
        if (!slideGroup.active) return;
        const slide = slideGroup.slides.get(slideGroup.active);
        slide.setDirection(Direction.VERTICAL);
        slide.setActive(false);

        slideGroup.active = null;
        slideGroup.history.length = 0;
        resetHash();
        window.scrollTo(0, slideGroup.scrollY);
    };

    const clickClose = () => queue(close());

    const next = () => {
        const current = slideGroup.slides.get(slideGroup.active);
        const nextSlide = getNextSlide(slideGroup.slides, slideGroup.active);
        slideGroup.history.push(nextSlide.id);

        nextSlide.setDirection(Direction.HORIZONTAL);
        delay(() => nextSlide.setActive(true), TRANSITION_DURATION);

        current.setDirection(Direction.VERTICAL);
        delay(() => current.setActive(false), TRANSITION_DURATION * 3);
        slideGroup.active = nextSlide.id;
    };

    // TODO: sequential vs priority next?
    const clickNext = () => {
        const nextSlide = getNextSlide(slideGroup.slides, slideGroup.active);
        if (!nextSlide) {
            return queue(close());
        }
        queue(next());
        pushBrowserHistory(nextSlide.id);
    };

    const prev = () => {
        slideGroup.history.pop();
        const historyLength = slideGroup.history.length;
        if (historyLength === 0) {
            return close();
        }
        const current = slideGroup.slides.get(slideGroup.active);
        const prevSlide = slideGroup.slides.get(historyLength > 0 ? slideGroup.history[historyLength - 1] : null);

        prevSlide.setEnter(false);
        prevSlide.setActive(true);
        delay(() => prevSlide.setEnter(true));

        current.setDirection(Direction.HORIZONTAL);
        delay(() => current.setActive(false), TRANSITION_DURATION);

        slideGroup.active = prevSlide.id;
    };

    const clickPrev = () => {
        window.history.back();
        queue(prev());
    };

    return (
        <SlideGroupContext.Provider value={{
            close: clickClose,
            getCurrent,
            isOpenedWithEmptyFields: startedWithEmptyFields,
            next: clickNext,
            open: clickOpen,
            prev: clickPrev,
            register,
        }}
        >
            {children}
        </SlideGroupContext.Provider>
    );
};

SlideGroupContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
